import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { getWorkDomainFromEmail, type Organization } from '@gitbook/app-database';

import { apiQuery } from '../api';
import { useCurrentUser } from '../auth/provider';

/**
 * List all organizations that the current user is going to join:
 *  - already joined
 *  - will join when the cloud function is executed
 *
 * @param ignoreOrgs List of organizations to ignore, e.g newly created by the user.
 */
export function useUserOrganizationsToJoin(
    ignoreOrgs: Organization[] = []
): Array<api.Organization & { isMember: boolean }> {
    const user = useCurrentUser();
    if (!user) {
        throw new Error('User is not logged in');
    }

    const emailDomain = React.useMemo(() => {
        return user.email && user.emailVerified ? getWorkDomainFromEmail(user.email) : null;
    }, [user.email, user.emailVerified]);

    const [{ data: membershipOrgs }, { data: emailDomainOrgs }] = useSuspenseQueries({
        queries: [
            apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }),
            apiQuery('getOrganizationsForEmailDomain', emailDomain ? { emailDomain } : skipToken),
        ],
    });

    const membershipOrgsIds = new Set(membershipOrgs.items.map(({ id }) => id) ?? []);

    const nonMembershipOrgs = (emailDomainOrgs?.organizations ?? []).filter(
        ({ id }) => !membershipOrgsIds.has(id)
    );

    return (
        [...membershipOrgs.items, ...nonMembershipOrgs]
            // Ignore any newly created orgs to prevent a crash in OnboardingScreen
            .filter((org) => !ignoreOrgs.some((ignoreOrg) => ignoreOrg.key === org.id))
            .map((org) => ({ ...org, isMember: membershipOrgsIds.has(org.id) }))
    );
}
